import React, { useState, useEffect } from 'react';
import { app } from './firebase';
import { getFirestore, doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore'; 
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';

const db = getFirestore(app);

export default function KanbanBoardView({ employees, user, logAction, navigateToConsultant, isSuperAdmin, isReadOnly, customers, consultantDb, addToast }) {
  const [activeView, setActiveView] = useState('recruitment'); // 'recruitment', 'active', 'bench'
  const [items, setItems] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState('All');
  const [loading, setLoading] = useState(false);
  
  // --- Quick Add State ---
  const [quickAddColumn, setQuickAddColumn] = useState(null);
  const [quickAddTitle, setQuickAddTitle] = useState("");

  // --- Modal States ---
  const [selectedItem, setSelectedItem] = useState(null);
  const [activeTab, setActiveTab] = useState('activity'); // 'activity', 'scorecards'
  
  // Schedule Modal State
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleData, setScheduleData] = useState({ strategy: 'recurring', frequencyValue: 1, frequencyUnit: 'months', specificDate: '' });
  const [savingSchedule, setSavingSchedule] = useState(false);

  // Snooze / Create States
  const [snoozeModalItem, setSnoozeModalItem] = useState(null);
  const [snoozeDate, setSnoozeDate] = useState('');
  const [noteText, setNoteText] = useState('');
  
  // Create Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newItemForm, setNewItemForm] = useState({ 
      title: '', subtitle: '', assigneeId: '', status: '', 
      linkType: 'None', relatedId: '' 
  });
  const [creating, setCreating] = useState(false);

  // Detail View Data
  const [childItems, setChildItems] = useState([]);
  const [consultantDetails, setConsultantDetails] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null); 
  const [linkedMaster, setLinkedMaster] = useState(null);

  // --- CONFIGURATION ---
  const BOARDS = {
    recruitment: {
      columns: ['To Do', 'Screening', 'CV Sent', 'Interview', 'Offer', 'Placed', 'Need to Contact', 'Done'],
      title: 'Recruitment Pipeline'
    },
    active: {
      columns: ['To Do', 'Scheduled', 'Done'],
      title: 'Active Consultant Management'
    },
    bench: {
      columns: ['Due for Outreach', 'Outreach Sent', 'Conversation', 'Active Pipeline', 'Snooze', 'Archive'],
      title: 'The Bench (Talent Pool)'
    }
  };

  // Soft Column Colors (Cycling)
  const COLUMN_ACCENTS = [
      '#a855f7', // Purple
      '#3b82f6', // Blue
      '#f59e0b', // Amber
      '#ec4899', // Pink
      '#10b981', // Emerald
      '#6366f1', // Indigo
      '#ef4444', // Red
      '#8b5cf6'  // Violet
  ];

  // --- HELPER: GET DYNAMIC COLOR ---
  const getItemColor = (assigneeId) => {
      const emp = employees.find(e => e.id === assigneeId);
      return emp?.profileColor || '#6c757d'; // Default grey if no assignee or no color set
  };

  // --- HELPER: TAG BADGE STYLES ---
  const getTagClass = (tag) => {
      const t = tag.toLowerCase();
      if (t.includes('vacancy')) return 'bg-warning text-dark'; // Gold/Yellow
      if (t.includes('application')) return 'bg-info text-dark'; // Cyan/Blue
      if (t.includes('manual')) return 'bg-secondary bg-opacity-75'; // Grey
      return 'bg-secondary bg-opacity-25 text-body'; // Default
  };

  // --- DATA FETCHING (SECURE) ---
  const fetchBoardData = async () => {
      setLoading(true);
      try {
          const functions = getFunctions(getApp(), 'europe-west1');
          const getItemsFn = httpsCallable(functions, 'getKanbanItems');
          
          const result = await getItemsFn();
          const allItems = result.data.items || [];

          // Client-side filtering
          const viewItems = allItems.filter(i => i.type === activeView);
          const visibleItems = viewItems.filter(i => showArchived ? true : !i.isArchived);
          
          // Sort by Due Date
          visibleItems.sort((a, b) => new Date(a.dueDate || '9999-12-31') - new Date(b.dueDate || '9999-12-31'));
          
          setItems(visibleItems);
      } catch (err) {
          console.error("Error fetching board data:", err);
          if (addToast) addToast("Failed to load board data", "error");
      } finally {
          setLoading(false);
      }
  };

  useEffect(() => {
      fetchBoardData();
      const interval = setInterval(fetchBoardData, 30000); 
      return () => clearInterval(interval);
  }, [activeView, showArchived]);

  // --- FETCH DETAILS ON MODAL OPEN ---
  useEffect(() => {
      if (selectedItem) {
          // Reset previous details
          setConsultantDetails(null);
          setCustomerDetails(null);
          setChildItems([]);
          setLinkedMaster(null);

          if (selectedItem.isMaster) {
              // CASE A: Master Item (Vacancy)
              const children = items.filter(i => i.masterId === selectedItem.relatedId);
              setChildItems(children);
          } 
          else if (selectedItem.relatedId) {
              // CASE B: Linked Item
              
              // 1. Check for Parent
              if (selectedItem.masterId) {
                  const master = items.find(i => i.isMaster && i.relatedId === selectedItem.masterId);
                  setLinkedMaster(master || null);
              }

              // 2. Fetch Details based on Type
              const fetchDetails = async () => {
                  try {
                      // Handle Customer Link
                      if (selectedItem.relatedType === 'Customer') {
                          const docRef = doc(db, "customers", selectedItem.relatedId);
                          const snap = await getDoc(docRef);
                          if (snap.exists()) {
                              setCustomerDetails(snap.data());
                          }
                          return;
                      }

                      // Default: Handle Consultant Link
                      let docRef = doc(db, "consultant_db", selectedItem.relatedId);
                      let snap = await getDoc(docRef);
                      
                      // Fallback for Active Board (Legacy Contract ID link fix)
                      if (!snap.exists() && activeView === 'active') {
                          const contractRef = doc(db, "active_contracts", selectedItem.relatedId);
                          const contractSnap = await getDoc(contractRef);
                          if (contractSnap.exists()) {
                              const consultantId = contractSnap.data().consultantId;
                              if (consultantId) {
                                  docRef = doc(db, "consultant_db", consultantId);
                                  snap = await getDoc(docRef);
                              }
                          }
                      }

                      if (snap.exists()) {
                          setConsultantDetails(snap.data());
                      }
                  } catch (e) { console.error("Error fetching details", e); }
              };
              fetchDetails();
          }
      } else {
          // Reset on Close
          setChildItems([]);
          setConsultantDetails(null);
          setCustomerDetails(null);
          setLinkedMaster(null);
          setActiveTab('activity');
      }
  }, [selectedItem, items, activeView]);

  // --- HANDLER: OPEN SCHEDULE MODAL ---
  const openScheduleModal = () => {
      if (!consultantDetails) return;
      
      let initial = { strategy: 'recurring', frequencyValue: 1, frequencyUnit: 'months', specificDate: '' };
      
      if (consultantDetails.checkInStrategy) {
          initial.strategy = consultantDetails.checkInStrategy;
          if (initial.strategy === 'specific') initial.specificDate = consultantDetails.nextCheckInDate || '';
      }
      
      if (consultantDetails.checkInFrequency) {
          initial.frequencyValue = consultantDetails.checkInFrequency.value;
          initial.frequencyUnit = consultantDetails.checkInFrequency.unit;
      } else {
          if (activeView === 'active') { initial.frequencyValue = 1; initial.frequencyUnit = 'months'; } 
          else { initial.frequencyValue = 6; initial.frequencyUnit = 'months'; } 
      }

      setScheduleData(initial);
      setShowScheduleModal(true);
  };

  // --- HANDLER: SAVE SCHEDULE ---
  const handleSaveSchedule = async () => {
      if (!selectedItem.relatedId) return;
      setSavingSchedule(true);
      try {
          const functions = getFunctions(getApp(), 'europe-west1');
          await httpsCallable(functions, 'updateCheckInSchedule')({
              consultantId: consultantDetails ? consultantDetails.id : selectedItem.relatedId, 
              ...scheduleData
          });
          if (addToast) addToast("Schedule updated successfully!", "success");
          setShowScheduleModal(false);
      } catch (err) { 
          if (addToast) addToast("Failed: " + err.message, "error"); 
      } finally { 
          setSavingSchedule(false); 
      }
  };

  // --- HANDLER: DRAG AND DROP ---
  const handleDrop = async (e, targetColumn) => {
    e.preventDefault();
    const itemId = e.dataTransfer ? e.dataTransfer.getData("itemId") : selectedItem?.id;
    if (!itemId) return;

    const item = items.find(i => i.id === itemId);
    if (!item) return;

    // Optimistic UI Update
    setItems(prev => prev.map(i => i.id === itemId ? { ...i, status: targetColumn } : i));
    if (selectedItem && selectedItem.id === itemId) {
        setSelectedItem(prev => ({ ...prev, status: targetColumn }));
    }

    if (activeView === 'bench' && targetColumn === 'Snooze') {
      setSnoozeModalItem(item);
      return;
    }

    let updates = { status: targetColumn };
    if (targetColumn === 'Done' || targetColumn === 'Placed') updates.doneAt = new Date().toISOString();

    try {
        const functions = getFunctions(getApp(), 'europe-west1');
        await httpsCallable(functions, 'updateKanbanItem')({ itemId, updates });
        if (addToast) addToast(`Moved to ${targetColumn}`, "info");
        
        // Note: Option 3 implementation does NOT sync global status here to allow multiple applications
    } catch (err) { 
        console.error(err);
        if (addToast) addToast("Move failed. Reverting...", "error");
        fetchBoardData(); 
    }
  };

  // --- HANDLER: CREATE ITEM ---
  const handleCreateItem = async (e) => {
      e.preventDefault();
      if(!newItemForm.title) return;
      
      setCreating(true);
      try {
          const assignee = employees.find(emp => emp.id === newItemForm.assigneeId);
          const payload = {
              title: newItemForm.title,
              subtitle: newItemForm.subtitle,
              type: activeView,
              status: newItemForm.status || BOARDS[activeView].columns[0],
              assigneeId: newItemForm.assigneeId,
              assigneeName: assignee ? assignee.name : 'Unassigned',
              assigneeImage: assignee ? assignee.imageFile : '',
              relatedId: newItemForm.relatedId || null, 
              relatedType: newItemForm.linkType === 'None' ? null : newItemForm.linkType,
              tags: ['Manual']
          };
          
          const functions = getFunctions(getApp(), 'europe-west1');
          await httpsCallable(functions, 'createKanbanItem')(payload);
          
          if (addToast) addToast("Card created successfully", "success");
          setShowCreateModal(false);
          setNewItemForm({ title: '', subtitle: '', assigneeId: '', status: '', linkType: 'None', relatedId: '' });
          fetchBoardData(); 
      } catch (err) { 
          if (addToast) addToast("Failed to create card", "error"); 
      } finally { 
          setCreating(false); 
      }
  };

  // --- HANDLER: QUICK ADD ---
  const handleQuickAdd = async (column) => {
      if(!quickAddTitle.trim()) return;
      
      const payload = {
          title: quickAddTitle,
          subtitle: 'Quick Task',
          type: activeView,
          status: column,
          assigneeName: 'Unassigned',
          tags: ['Quick Add']
      };
      
      // Optimistic UI
      const tempId = Date.now().toString();
      setItems(prev => [...prev, { ...payload, id: tempId }]);
      setQuickAddTitle("");
      setQuickAddColumn(null);

      try {
          const functions = getFunctions(getApp(), 'europe-west1');
          await httpsCallable(functions, 'createKanbanItem')(payload);
          if (addToast) addToast("Task created", "success");
          fetchBoardData(); // Sync real ID
      } catch(err) {
          if (addToast) addToast("Failed to create task", "error");
          setItems(prev => prev.filter(i => i.id !== tempId));
      }
  };

  // --- HANDLER: SNOOZE & ARCHIVE ---
  const handleSnoozeConfirm = async (type) => {
      if (!snoozeModalItem) return;
      
      let nextDate = new Date();
      if (type === 'standard') nextDate.setMonth(nextDate.getMonth() + 6);
      else {
          if (!snoozeDate) return;
          nextDate = new Date(snoozeDate);
      }

      try {
          const functions = getFunctions(getApp(), 'europe-west1');
          await httpsCallable(functions, 'deleteKanbanItem')({ itemId: snoozeModalItem.id });
          setItems(prev => prev.filter(i => i.id !== snoozeModalItem.id));
          if (addToast) addToast("Item snoozed", "warning");
      } catch(err) { 
          if (addToast) addToast("Failed to snooze", "error"); 
      }
      
      setSnoozeModalItem(null);
      setSnoozeDate('');
  };

  const handleArchiveItem = async () => {
      if (!selectedItem) return;
      if (isReadOnly) return;
      
      if(!window.confirm("Manually archive this item?")) return;

      try {
          const functions = getFunctions(getApp(), 'europe-west1');
          await httpsCallable(functions, 'updateKanbanItem')({ itemId: selectedItem.id, updates: { isArchived: true } });
          setItems(prev => prev.filter(i => i.id !== selectedItem.id));
          setSelectedItem(null);
          if (addToast) addToast("Item archived", "info");
      } catch (err) { 
          if (addToast) addToast("Failed to archive", "error"); 
      }
  };

  // --- HANDLER: ASSIGN ---
  const handleAssign = async (empId) => {
      const emp = employees.find(e => e.id === empId);
      const updates = { assigneeId: empId, assigneeName: emp ? emp.name : 'Unassigned', assigneeImage: emp ? emp.imageFile : '' };
      setSelectedItem(prev => ({...prev, ...updates}));
      setItems(prev => prev.map(i => i.id === selectedItem.id ? { ...i, ...updates } : i));
      try {
          const functions = getFunctions(getApp(), 'europe-west1');
          await httpsCallable(functions, 'updateKanbanItem')({ itemId: selectedItem.id, updates });
      } catch(err) { fetchBoardData(); }
  };

  // --- HANDLER: ADD NOTE (WITH SYNC) ---
  const handleAddNote = async () => {
      if(!noteText.trim() || !selectedItem) return;
      const note = { text: noteText, author: user.email, date: new Date().toISOString() };
      
      // 1. Local Update
      const newNotes = [...(selectedItem.internalNotes || []), note];
      setSelectedItem(prev => ({...prev, internalNotes: newNotes}));
      setItems(prev => prev.map(i => i.id === selectedItem.id ? { ...i, internalNotes: newNotes } : i));

      try {
          // 2. Update Kanban Item
          const functions = getFunctions(getApp(), 'europe-west1');
          await httpsCallable(functions, 'updateKanbanItem')({ itemId: selectedItem.id, updates: { internalNotes: newNotes } });

          // 3. COPY TO CONSULTANT PROFILE
          if (selectedItem.relatedId && selectedItem.relatedType !== 'Customer') {
              const consRef = doc(db, "consultant_db", selectedItem.relatedId);
              await updateDoc(consRef, {
                  notesLog: arrayUnion(note)
              });
          }

      } catch(err) { fetchBoardData(); }
      setNoteText('');
  };

  // --- HELPERS ---
  const getDueDateStatus = (dateStr) => {
      if(!dateStr) return 'none';
      const due = new Date(dateStr);
      const today = new Date(); today.setHours(0,0,0,0); due.setHours(0,0,0,0);
      const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
      if (diffDays < 0) return 'overdue'; if (diffDays === 0) return 'today'; if (diffDays === 1) return 'soon';
      return 'ok';
  };

  const displayItems = filterEmployee === 'All' ? items : items.filter(i => i.assigneeId === filterEmployee);

  return (
    <div className="container-fluid p-0 h-100 d-flex flex-column">
      
      {/* 1. HEADER & CONTROLS */}
      <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center gap-3">
              <div className="btn-group">
                  {Object.keys(BOARDS).map(k => (
                      <button 
                        key={k} 
                        className={`btn btn-sm ${activeView === k ? 'btn-primary' : 'btn-outline-secondary'}`} 
                        onClick={() => setActiveView(k)}
                      >
                          {BOARDS[k].title}
                      </button>
                  ))}
              </div>
          </div>
          
          <div className="d-flex gap-2 align-items-center">
              {!isReadOnly && (
                  <button className="btn btn-primary btn-sm" onClick={() => { setNewItemForm({...newItemForm, status: BOARDS[activeView].columns[0]}); setShowCreateModal(true); }}>
                      <i className="bi bi-plus-lg me-2"></i> New
                  </button>
              )}
              
              <button 
                  className={`btn btn-sm ${showArchived ? 'btn-warning' : 'btn-outline-secondary'}`} 
                  onClick={() => setShowArchived(!showArchived)}
                  title={showArchived ? "Hide Archived Items" : "Show Archived Items"}
              >
                  <i className="bi bi-archive me-1"></i> {showArchived ? 'Hide' : 'Archived'}
              </button>

              <select className="form-select bg-card text-main border-theme w-auto" value={filterEmployee} onChange={e=>setFilterEmployee(e.target.value)}>
                  <option value="All">All Team</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
              <button className="btn btn-outline-secondary" onClick={fetchBoardData}><i className="bi bi-arrow-clockwise"></i></button>
          </div>
      </div>

      {/* 2. KANBAN BOARD */}
      <div className="flex-grow-1 overflow-auto d-flex gap-3 pb-3">
          {BOARDS[activeView].columns.map((col, idx) => {
              const colItems = displayItems.filter(i => i.status === col);
              // Cycling specific colors for visual distinction (Zapier style)
              const accentColor = COLUMN_ACCENTS[idx % COLUMN_ACCENTS.length];

              return (
                  <div key={col} className="d-flex flex-column flex-shrink-0" style={{width: '320px'}}>
                      {/* HEADER */}
                      <div className="d-flex justify-content-between align-items-center mb-2 px-3 py-2 bg-input border-theme rounded-top border-bottom-0" 
                           style={{borderTop: `3px solid ${accentColor}`}}>
                          <div className="d-flex align-items-center gap-2">
                              <div className="rounded-circle" style={{width: '8px', height: '8px', backgroundColor: accentColor}}></div>
                              <span className="fw-bold text-main text-uppercase small" style={{letterSpacing: '0.5px'}}>{col}</span>
                          </div>
                          <span className="badge bg-secondary bg-opacity-25 text-muted-theme border border-theme">{colItems.length}</span>
                      </div>
                      
                      {/* COLUMN BODY */}
                      <div className="rounded-bottom p-2 flex-grow-1 overflow-auto border border-theme border-top-0" 
                           onDragOver={(e) => e.preventDefault()} 
                           onDrop={(e) => handleDrop(e, col)}
                           style={{
                               minHeight: '200px', 
                               backgroundColor: 'rgba(128, 128, 128, 0.05)' // Subtle background for swimlane distinction
                           }}>
                          
                          {colItems.map(item => {
                              const assignedEmp = employees.find(e => e.id === item.assigneeId);
                              const displayImage = assignedEmp?.imageFile || item.assigneeImage;
                              
                              const cardColor = getItemColor(item.assigneeId);
                              const displayName = (['Unassigned', 'Not assigned'].includes(item.assigneeName) || !item.assigneeName) 
                                                  ? 'Unassigned' 
                                                  : item.assigneeName.split(' ')[0];

                              const cardOpacity = item.isArchived ? '0.6' : '1';
                              const noteCount = item.internalNotes ? item.internalNotes.length : 0;

                              return (
                                  <div 
                                      key={item.id} 
                                      draggable 
                                      onDragStart={(e) => e.dataTransfer.setData("itemId", item.id)} 
                                      onClick={() => setSelectedItem(item)}
                                      className="card mb-2 shadow-sm bg-card border-theme"
                                      style={{
                                          cursor: 'grab', 
                                          border: `2px solid ${cardColor}`, 
                                          opacity: cardOpacity,
                                          transition: 'transform 0.2s, box-shadow 0.2s'
                                      }}
                                      onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                                      onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                                  >
                                      <div className="card-body p-3">
                                          {/* TAGS & ICONS */}
                                          <div className="mb-2 d-flex flex-wrap gap-1">
                                              {item.isMaster && <i className="bi bi-star-fill text-warning me-1" title="Master Vacancy Item"></i>}
                                              {item.isArchived && <i className="bi bi-archive-fill text-secondary me-1" title="Archived"></i>}
                                              {item.tags && item.tags.map((t, idx) => (
                                                  <span key={idx} className={`badge ${getTagClass(t)} me-1`} style={{fontSize: '0.65rem', fontWeight: '500'}}>
                                                      {t}
                                                  </span>
                                              ))}
                                          </div>
                                          
                                          <h6 className="card-title fw-bold text-main mb-1 text-truncate">{item.title}</h6>
                                          <p className="card-text small text-muted-theme mb-3 text-truncate">{item.subtitle}</p>
                                          
                                          {/* PROGRESS BAR & AVATAR (ZAPIER STYLE) */}
                                      <div className="d-flex align-items-center justify-content-between mt-2 pt-2 border-top border-secondary border-opacity-10">
                                          
                                          {/* Avatar & Name */}
                                          <div className="d-flex align-items-center" style={{maxWidth: '50%'}}>
                                              <div className="kanban-avatar-stack me-2 flex-shrink-0">
                                                  {displayImage ? (
                                                      <img src={displayImage} className="rounded-circle" style={{width:24, height:24, objectFit:'cover'}} alt="Assigned"/>
                                                  ) : (
                                                      <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white small" style={{width:24, height:24}}>
                                                          {displayName === 'Unassigned' ? '?' : displayName.charAt(0)}
                                                      </div>
                                                  )}
                                              </div>
                                              <span className="text-muted-theme text-truncate small" style={{fontSize: '0.75rem'}} title={item.assigneeName}>
                                                  {displayName}
                                              </span>
                                          </div>

                                          <div className="d-flex align-items-center gap-2 justify-content-end" style={{maxWidth: '50%'}}>
                                              {/* Note Count Indicator */}
                                              {noteCount > 0 && (
                                                  <div className="d-flex align-items-center text-muted-theme small bg-input px-1 rounded border border-theme flex-shrink-0" title={`${noteCount} Notes`}>
                                                      <i className="bi bi-chat-left-text me-1" style={{fontSize: '0.7rem'}}></i>
                                                      <span style={{fontSize: '0.7rem'}}>{noteCount}</span>
                                                  </div>
                                              )}
                                              
                                              {/* Dynamic Progress Score based on Column Stage */}
                                              {activeView === 'recruitment' && (
                                                  <div className="d-flex align-items-center gap-1" style={{width: '50px'}}>
                                                      <div className="kanban-progress flex-grow-1" style={{marginTop: 0}}>
                                                          <div 
                                                              className="kanban-progress-bar" 
                                                              style={{width: `${Math.round(((BOARDS[activeView].columns.indexOf(item.status) + 1) / BOARDS[activeView].columns.length) * 100)}%`}}
                                                          ></div>
                                                      </div>
                                                  </div>
                                              )}
                                          </div>
                                      </div>
                                      </div>
                                  </div>
                              );
                          })}

                          {/* QUICK ADD BUTTON */}
                          {quickAddColumn === col ? (
                              <div className="card p-2 border-theme bg-input animate__animated animate__fadeIn">
                                  <input 
                                      autoFocus 
                                      className="form-control form-control-sm mb-2" 
                                      placeholder="Task Title..." 
                                      value={quickAddTitle} 
                                      onChange={e => setQuickAddTitle(e.target.value)} 
                                      onKeyDown={e => e.key === 'Enter' && handleQuickAdd(col)} 
                                  />
                                  <div className="d-flex gap-2">
                                      <button className="btn btn-sm btn-primary flex-grow-1" onClick={() => handleQuickAdd(col)}>Add</button>
                                      <button className="btn btn-sm btn-outline-secondary" onClick={() => setQuickAddColumn(null)}>X</button>
                                  </div>
                              </div>
                          ) : (
                              <button 
                                  className="btn btn-sm btn-ghost w-100 text-muted-theme mt-2" 
                                  onClick={() => setQuickAddColumn(col)}
                                  style={{borderStyle: 'dashed'}}
                              >
                                  <i className="bi bi-plus-lg me-1"></i> Add Task
                              </button>
                          )}
                      </div>
                  </div>
              );
          })}
      </div>

      {/* 3. MODALS */}
      
      {/* DETAIL MODAL */}
      {selectedItem && (
          <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.6)'}}>
              <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                  <div className="modal-content bg-card border-theme shadow-lg">
                      
                      {/* CARD HEADER */}
                      <div className="modal-header border-bottom border-theme flex-column align-items-stretch p-0">
                          <div className="d-flex justify-content-between align-items-center p-4">
                              <div>
                                  <h3 className="modal-title fw-bold text-main mb-1">
                                      {selectedItem.title} 
                                      {selectedItem.isArchived && <span className="badge bg-secondary ms-2 small">Archived</span>}
                                  </h3>
                                  <p className="text-muted-theme mb-0">{selectedItem.subtitle} {linkedMaster && <span className="badge bg-secondary ms-2">Linked to: {linkedMaster.title}</span>}</p>
                              </div>
                              <div className="d-flex gap-2">
                                  <button className="btn btn-outline-secondary" onClick={() => setSelectedItem(null)}>Close</button>
                                  {/* Only show "Open Profile" for Consultants */}
                                  {consultantDetails && !selectedItem.isMaster && selectedItem.relatedId && (
                                      <button className="btn btn-primary" onClick={() => navigateToConsultant(selectedItem.relatedId)}>
                                          Open Profile <i className="bi bi-box-arrow-up-right ms-2"></i>
                                      </button>
                                  )}
                              </div>
                          </div>
                          
                          {/* STAGE STEPPER */}
                          <div className="d-flex bg-input border-top border-theme px-4 py-3 overflow-auto no-scrollbar align-items-center">
                              {BOARDS[activeView].columns.map((col, idx) => {
                                  const isCurrent = selectedItem.status === col;
                                  const isPast = BOARDS[activeView].columns.indexOf(selectedItem.status) > idx;
                                  
                                  return (
                                      <React.Fragment key={col}>
                                          <button 
                                              onClick={() => handleDrop({ preventDefault: () => {} }, col)} 
                                              className={`btn btn-sm rounded-pill px-3 fw-bold flex-shrink-0 border-0 ${
                                                  isCurrent 
                                                      ? 'btn-primary shadow' 
                                                      : (isPast ? 'btn-success bg-opacity-25 text-success' : 'text-muted-theme hover-bg-light')
                                              }`}
                                              style={{whiteSpace: 'nowrap', transition: 'all 0.2s'}}
                                          >
                                              {isCurrent && <i className="bi bi-check-circle-fill me-2"></i>}
                                              {col}
                                          </button>
                                          {idx < BOARDS[activeView].columns.length - 1 && (
                                              <div className="mx-2 border-top border-secondary opacity-25" style={{width: '20px'}}></div>
                                          )}
                                      </React.Fragment>
                                  );
                              })}
                          </div>
                      </div>

                      <div className="modal-body p-0">
                          <div className="row g-0 h-100" style={{minHeight: '500px'}}>
                              
                              {/* LEFT COLUMN: INFO */}
                              <div className="col-lg-4 border-end border-theme p-4 bg-input">
                                  {/* INFO SECTION */}
                                  <div className="mb-4">
                                      <h6 className="text-uppercase text-muted-theme small fw-bold mb-3">
                                          {customerDetails ? 'Customer Details' : (consultantDetails ? 'Candidate Details' : 'Details')}
                                      </h6>
                                      
                                      {customerDetails ? (
                                          // --- CUSTOMER DETAILS VIEW ---
                                          <div className="d-flex flex-column gap-3">
                                              <div className="d-flex align-items-center gap-3">
                                                  <div className="rounded bg-primary bg-opacity-10 d-flex align-items-center justify-content-center text-primary h2 mb-0" style={{width:64, height:64}}>
                                                      <i className="bi bi-building"></i>
                                                  </div>
                                                  <div>
                                                      <div className="fw-bold text-main">{customerDetails.companyName}</div>
                                                      <div className="text-muted-theme small">{customerDetails.industry || 'No Industry'}</div>
                                                  </div>
                                              </div>
                                              <div>
                                                  <label className="small text-muted-theme d-block">Contact</label>
                                                  <div className="text-main">{customerDetails.contactName}</div>
                                                  <div className="text-main small">{customerDetails.contactEmail}</div>
                                                  <div className="text-main small">{customerDetails.contactPhone}</div>
                                              </div>
                                              <div className="d-grid gap-2">
                                                  <a href={`mailto:${customerDetails.contactEmail}`} className="btn btn-outline-primary btn-sm"><i className="bi bi-envelope me-2"></i> Send Email</a>
                                              </div>
                                          </div>
                                      ) : consultantDetails ? (
                                          // --- CONSULTANT DETAILS VIEW ---
                                          <div className="d-flex flex-column gap-3">
                                              <div className="d-flex align-items-center gap-3">
                                                  {consultantDetails.imageFile ? (
                                                      <img src={consultantDetails.imageFile} className="rounded-circle border border-theme" style={{width:64, height:64, objectFit:'cover'}} alt="Profile"/>
                                                  ) : (
                                                      <div className="rounded-circle bg-secondary d-flex align-items-center justify-content-center text-white h2 mb-0" style={{width:64, height:64}}>{consultantDetails.firstName?.charAt(0)}</div>
                                                  )}
                                                  <div className="overflow-hidden">
                                                      <div className="fw-bold text-main text-truncate">{consultantDetails.email}</div>
                                                      <div className="text-muted-theme small">{consultantDetails.phone}</div>
                                                      <div className="text-muted-theme small">{consultantDetails.city}, {consultantDetails.country}</div>
                                                  </div>
                                              </div>
                                              
                                              {consultantDetails.hardSkills && (
                                                  <div className="bg-card p-2 rounded border border-theme">
                                                      <label className="small text-muted-theme d-block mb-1">Skills</label>
                                                      <p className="text-main small mb-0">{consultantDetails.hardSkills}</p>
                                                  </div>
                                              )}

                                              <div className="d-grid gap-2">
                                                  <a href={`mailto:${consultantDetails.email}`} className="btn btn-outline-primary btn-sm"><i className="bi bi-envelope me-2"></i> Send Email</a>
                                                  {consultantDetails.resumes && consultantDetails.resumes.length > 0 && (
                                                      <a href={consultantDetails.resumes[0].url} target="_blank" rel="noreferrer" className="btn btn-outline-secondary btn-sm"><i className="bi bi-file-pdf me-2"></i> View Resume</a>
                                                  )}
                                              </div>
                                          </div>
                                      ) : selectedItem.isMaster ? (
                                          <div className="text-center py-4 text-muted-theme">
                                              <i className="bi bi-briefcase fs-1 mb-2"></i>
                                              <p>Vacancy Item</p>
                                          </div>
                                      ) : (
                                          <div className="text-center py-4 text-muted-theme">
                                              <div className="spinner-border spinner-border-sm mb-2"></div>
                                              <p>Loading details...</p>
                                          </div>
                                      )}
                                  </div>

                                  <hr className="border-theme"/>

                                  {/* OWNERSHIP SECTION */}
                                  <div className="mb-4">
                                      <h6 className="text-uppercase text-muted-theme small fw-bold mb-3">Ownership</h6>
                                      
                                      <div className="mb-3">
                                          <label className="small text-muted-theme mb-1">Assignee</label>
                                          <select className="form-select form-select-sm bg-card text-main border-theme" value={selectedItem.assigneeId} onChange={(e) => handleAssign(e.target.value)}>
                                              <option value="">Unassigned</option>
                                              {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                                          </select>
                                      </div>

                                      {/* BENCH SPECIFIC CONTROLS (Only for consultants) */}
                                      {consultantDetails && (activeView === 'active' || activeView === 'bench') && (
                                          <div className="d-grid gap-2 mb-3">
                                              <button className="btn btn-sm btn-outline-info" onClick={openScheduleModal}>
                                                  <i className="bi bi-calendar-event me-2"></i> Check-in Schedule
                                              </button>
                                              <button className="btn btn-sm btn-outline-warning" onClick={() => setSnoozeModalItem(selectedItem)}>
                                                  <i className="bi bi-alarm me-2"></i> Snooze
                                              </button>
                                          </div>
                                      )}
                                  </div>
                              </div>

                              {/* RIGHT COLUMN: ACTIVITY & NOTES */}
                              <div className="col-lg-8 p-4 bg-card d-flex flex-column">
                                  {/* TABS */}
                                  <ul className="nav nav-tabs border-theme mb-4">
                                      <li className="nav-item">
                                          <button className={`nav-link ${activeTab==='activity'?'active bg-input text-primary border-theme border-bottom-0':'text-muted-theme border-transparent'}`} onClick={()=>setActiveTab('activity')}>Activity & Notes</button>
                                      </li>
                                      {/* Only show Scorecards for Consultants */}
                                      {consultantDetails && (
                                          <li className="nav-item">
                                              <button className={`nav-link ${activeTab==='scorecards'?'active bg-input text-primary border-theme border-bottom-0':'text-muted-theme border-transparent'}`} onClick={()=>setActiveTab('scorecards')}>Scorecards</button>
                                          </li>
                                      )}
                                  </ul>

                                  {activeTab === 'activity' && (
                                      <div className="d-flex flex-column h-100">
                                          {/* NOTE INPUT */}
                                          <div className="bg-input p-3 rounded border border-theme mb-4 shadow-sm">
                                              <textarea 
                                                  className="form-control bg-transparent border-0 text-main" 
                                                  placeholder="Write a note... (@mention team members)" 
                                                  rows="3"
                                                  value={noteText}
                                                  onChange={e => setNoteText(e.target.value)}
                                                  style={{resize: 'none'}}
                                              ></textarea>
                                              <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top border-theme">
                                                  <div className="text-muted-theme small"><i className="bi bi-markdown me-1"></i> Markdown supported</div>
                                                  <button className="btn btn-sm btn-primary px-4" onClick={handleAddNote} disabled={!noteText.trim()}>Post Note</button>
                                              </div>
                                          </div>

                                          {/* FEED */}
                                          <div className="flex-grow-1 overflow-auto pe-2">
                                              {selectedItem.internalNotes && [...selectedItem.internalNotes].reverse().map((n, i) => (
                                                  <div key={i} className="d-flex gap-3 mb-4">
                                                      <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white flex-shrink-0 shadow-sm" style={{width:36, height:36}}>
                                                          {n.author.charAt(0).toUpperCase()}
                                                      </div>
                                                      <div className="flex-grow-1">
                                                          <div className="d-flex align-items-baseline gap-2 mb-1">
                                                              <span className="fw-bold text-main">{n.author}</span>
                                                              <small className="text-muted-theme" style={{fontSize: '0.75rem'}}>{new Date(n.date).toLocaleString()}</small>
                                                          </div>
                                                          <div className="bg-input p-3 rounded border border-theme text-main shadow-sm" style={{fontSize: '0.95rem'}}>
                                                              {n.text}
                                                          </div>
                                                      </div>
                                                  </div>
                                              ))}
                                              {(!selectedItem.internalNotes || selectedItem.internalNotes.length === 0) && (
                                                  <div className="text-center text-muted-theme py-5">
                                                      <i className="bi bi-chat-square-text fs-1 mb-2 d-block"></i>
                                                      No activity yet. Start the conversation!
                                                  </div>
                                              )}
                                          </div>
                                      </div>
                                  )}

                                  {activeTab === 'scorecards' && consultantDetails && (
                                      <div className="h-100 overflow-auto">
                                          {consultantDetails?.scorecards?.map((sc, idx) => (
                                              <div key={idx} className="card bg-input border-theme mb-3 shadow-sm">
                                                  <div className="card-body">
                                                      <div className="d-flex justify-content-between mb-2">
                                                          <h6 className="fw-bold text-main mb-0">{sc.interviewer}</h6>
                                                          <small className="text-muted-theme">{new Date(sc.date).toLocaleDateString()}</small>
                                                      </div>
                                                      <div className="mb-3">
                                                          <span className="badge bg-primary me-2">Tech: {sc.technical}/5</span>
                                                          <span className="badge bg-info text-dark">Culture: {sc.culture}/5</span>
                                                      </div>
                                                      <div className="p-2 bg-card rounded border border-theme">
                                                          <p className="text-main small mb-0 fst-italic">"{sc.notes}"</p>
                                                      </div>
                                                  </div>
                                              </div>
                                          ))}
                                          {(!consultantDetails?.scorecards || consultantDetails.scorecards.length === 0) && (
                                              <div className="text-center text-muted-theme py-5">
                                                  <i className="bi bi-clipboard-data fs-1 mb-2 d-block"></i>
                                                  No interview scorecards recorded.
                                              </div>
                                          )}
                                          {!selectedItem.isMaster && selectedItem.relatedId && (
                                              <button className="btn btn-outline-primary w-100 mt-3" onClick={() => navigateToConsultant(selectedItem.relatedId)}>
                                                  Go to Profile to Add Scorecard
                                              </button>
                                          )}
                                      </div>
                                  )}
                              </div>
                          </div>
                      </div>
                      
                      <div className="modal-footer border-theme bg-input justify-content-between">
                          {!isReadOnly && (
                              <button className="btn btn-sm btn-outline-warning" onClick={handleArchiveItem}>
                                  <i className="bi bi-archive me-2"></i> {selectedItem.isArchived ? 'Restore' : 'Archive'} Card
                              </button>
                          )}
                          <button className="btn btn-secondary" onClick={() => setSelectedItem(null)}>Close</button>
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* --- CREATE MODAL (Simple) --- */}
      {showCreateModal && (
          <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
              <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content bg-card border-theme">
                      <div className="modal-header border-theme">
                          <h5 className="modal-title text-main">New Card</h5>
                          <button className="btn-close" onClick={() => setShowCreateModal(false)}></button>
                      </div>
                      <div className="modal-body">
                          <form onSubmit={handleCreateItem}>
                              <div className="mb-3"><label className="small text-muted-theme">Title</label><input className="form-control bg-input text-main border-theme" required value={newItemForm.title} onChange={e => setNewItemForm({...newItemForm, title: e.target.value})} /></div>
                              <div className="mb-3"><label className="small text-muted-theme">Description</label><input className="form-control bg-input text-main border-theme" value={newItemForm.subtitle} onChange={e => setNewItemForm({...newItemForm, subtitle: e.target.value})} /></div>
                              
                              <div className="row g-2 mb-3">
                                  <div className="col-6">
                                      <label className="small text-muted-theme">Link To (Optional)</label>
                                      <select className="form-select bg-input text-main border-theme" value={newItemForm.linkType} onChange={e => setNewItemForm({...newItemForm, linkType: e.target.value, relatedId: ''})}>
                                          <option value="None">None</option>
                                          <option value="Consultant">Consultant</option>
                                          <option value="Customer">Customer</option>
                                      </select>
                                  </div>
                                  <div className="col-6">
                                      <label className="small text-muted-theme">Select Entity</label>
                                      <select className="form-select bg-input text-main border-theme" value={newItemForm.relatedId} onChange={e => setNewItemForm({...newItemForm, relatedId: e.target.value})} disabled={newItemForm.linkType === 'None'}>
                                          <option value="">Select...</option>
                                          {newItemForm.linkType === 'Consultant' && consultantDb.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>)}
                                          {newItemForm.linkType === 'Customer' && customers.map(c => <option key={c.id} value={c.id}>{c.companyName}</option>)}
                                      </select>
                                  </div>
                              </div>

                              <div className="mb-3">
                                  <label className="small text-muted-theme">Assign To</label>
                                  <select className="form-select bg-input text-main border-theme" value={newItemForm.assigneeId} onChange={e => setNewItemForm({...newItemForm, assigneeId: e.target.value})}>
                                      <option value="">Unassigned</option>
                                      {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                                  </select>
                              </div>
                              <button type="submit" className="btn btn-primary w-100" disabled={creating}>{creating ? 'Creating...' : 'Create'}</button>
                          </form>
                      </div>
                  </div>
              </div>
          </div>
      )}

      {/* SNOOZE & SCHEDULE MODALS */}
      {showScheduleModal && (
          <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060}}>
              <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content border-theme bg-card">
                      <div className="modal-header border-theme"><h5 className="modal-title text-main">Schedule Check-in</h5></div>
                      <div className="modal-body">
                          <label className="text-muted-theme small">Next Date</label>
                          <input type="date" className="form-control mb-3 bg-input text-main border-theme" value={scheduleData.specificDate} onChange={e => setScheduleData({...scheduleData, strategy: 'specific', specificDate: e.target.value})} />
                      </div>
                      <div className="modal-footer border-theme">
                          <button className="btn btn-secondary" onClick={()=>setShowScheduleModal(false)}>Cancel</button>
                          <button className="btn btn-success" onClick={handleSaveSchedule} disabled={savingSchedule}>{savingSchedule ? 'Saving' : 'Save'}</button>
                      </div>
                  </div>
              </div>
          </div>
      )}
      {snoozeModalItem && (
          <div className="modal show d-block" style={{backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060}}>
              <div className="modal-dialog modal-dialog-centered">
                  <div className="modal-content border-theme bg-card">
                      <div className="modal-header border-theme"><h5 className="modal-title text-main">Snooze</h5></div>
                      <div className="modal-body">
                          <p className="text-main">Snoozing removes this card from the active board until the selected date.</p>
                          <input type="date" className="form-control bg-input text-main border-theme" value={snoozeDate} onChange={e=>setSnoozeDate(e.target.value)} />
                      </div>
                      <div className="modal-footer border-theme">
                          <button className="btn btn-secondary" onClick={()=>setSnoozeModalItem(null)}>Cancel</button>
                          <button className="btn btn-warning" onClick={() => handleSnoozeConfirm('specific')}>Snooze</button>
                      </div>
                  </div>
              </div>
          </div>
      )}
    </div>
  );
}